import { FormHelperText, MenuItem, Select } from "@mui/material";
import React from "react";
import { MuiBox } from "..";
import { GroupedOption } from "../../helpers/types/common";
import { getHelperText, removeWhiteSpace } from "../../helpers/utils";
import { SeparatorDotted } from "../../styles/common.style";
import { COLORS } from "../../styles/constants.style";
import { ErrorCrossIcon } from "../errorCrossIcon/ErrorCrossIcon";
import { MuiTypography } from "../typography/MuiTypography";
import GroupedSelectRenderValue from "./components/GroupedSelectRenderValue";
import GroupedSelectSearchInput from "./components/GroupedSelectSearchInput";
import useMuiGroupedSelect from "./hooks/useMuiGroupedSelect";
import {
  GroupedSelectListSubheader,
  MuiGroupedSelectWrapper,
  searchWithGroupedInputStyle,
} from "./MuiGroupedSelect.style";

export type MuiGroupedSelectProps = {
  id: string;
  label: string;
  options: GroupedOption[];
  className?: string;
  helperText?: string;
  isMandatory?: boolean;
  disabled?: boolean;
  showSearch?: boolean;
  searchPlaceholder?: string;
  disabledItems?: string[];
  requiredARIALabel?: string;
  defaultPlaceholder?: string;
  isErrorIconEnabled?: boolean;
  [key: string]: any;
};

export const MuiGroupedSelect: React.FC<MuiGroupedSelectProps> = ({
  label,
  options = [],
  className = "",
  helperText,
  isMandatory,
  disabled,
  showSearch = false,
  searchPlaceholder = "Search",
  id,
  defaultPlaceholder = "Select",
  isErrorIconEnabled = true,
  disabledItems = [],
  requiredARIALabel = "Required",
  ...rest
}) => {
  const {
    filterText,
    menuWidth,
    handleSearchFilterChange,
    handleSearchKeyDown,
    handleOpen,
    handleInputClick,
    filteredGroupMap,
    isSingleGroup,
  } = useMuiGroupedSelect(options, showSearch);

  return (
    <MuiGroupedSelectWrapper className={className}>
      {label && (
        <MuiBox className="title">
          <MuiTypography component="label" variant="h3" className="mui-label">
            {label}{" "}
            {isMandatory && !disabled && (
              <label className="asterisk" aria-label={requiredARIALabel}>
                *
              </label>
            )}
          </MuiTypography>
        </MuiBox>
      )}
      <Select
        displayEmpty
        disabled={disabled}
        onOpen={showSearch ? handleOpen : undefined}
        MenuProps={{
          disableAutoFocusItem: showSearch,
          PaperProps: {
            sx: showSearch
              ? { width: menuWidth, ...searchWithGroupedInputStyle }
              : {},
          },
        }}
        renderValue={(value) => {
          const stringValue = value as string;
          if (!stringValue) {
            return (
              <MuiTypography
                variant="body3SemiBold"
                color={COLORS.cirrus_black5}
              >
                {defaultPlaceholder}
              </MuiTypography>
            );
          }
          return (
            <GroupedSelectRenderValue
              value={value as string | number | undefined}
              options={options}
              placeholder={rest.placeholder}
            />
          );
        }}
        {...rest}
        inputProps={{
          "data-testid": id,
          "aria-label": `${label} ${isMandatory && !disabled ? requiredARIALabel : ""}`,
          ...rest.inputProps,
        }}
        className="mui-select"
      >
        {showSearch && (
          <GroupedSelectSearchInput
            filterText={filterText}
            onChange={handleSearchFilterChange}
            onKeyDown={handleSearchKeyDown}
            onClick={handleInputClick}
            placeholder={searchPlaceholder}
            requiredARIALabel={requiredARIALabel}
          />
        )}
        {Object.entries(filteredGroupMap).flatMap(
          ([group, opts], groupIdx, arr) => [
            ...(!isSingleGroup && opts.length
              ? [
                  <GroupedSelectListSubheader key={group}>
                    {group}
                  </GroupedSelectListSubheader>,
                ]
              : []),
            ...(opts.length
              ? opts.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.id}
                    disabled={disabledItems?.includes(option.id)}
                  >
                    <MuiTypography>{option.name}</MuiTypography>
                  </MenuItem>
                ))
              : []),
            groupIdx < arr.length - 1 ? (
              <li
                key={`sep-${group}`}
                role="presentation"
                aria-hidden="true"
                className="separator-dotted-li"
              >
                <SeparatorDotted />
              </li>
            ) : null,
          ],
        )}
      </Select>
      {rest.error && (
        <FormHelperText error={rest.error}>
          {helperText && (
            <MuiBox
              role="alert"
              aria-description={getHelperText(helperText, label)}
              className="error-icon"
            >
              {isErrorIconEnabled && <ErrorCrossIcon />}
              <div aria-hidden id={`helper-text-${removeWhiteSpace(label)}`}>
                {getHelperText(helperText, label)}
              </div>
            </MuiBox>
          )}
        </FormHelperText>
      )}
    </MuiGroupedSelectWrapper>
  );
};
